import { metricNameToKey } from './metric-key.util';

/**
 * The metrics a dataset may declare, per problem statement: exactly what that problem type's
 * evaluation script reports. Anything else cannot be scored, so the onboarding form only offers
 * (and only accepts) these.
 *
 * Sources:
 *   binary / multiclass / segmentation -- TANUH-processingtee `evaluators/<type>/evaluate.py`
 *   bounding box                       -- the RHV kidney benchmark's `Evaluation/evaluate.py`
 */
export interface EvaluatorMetricOption {
  /** Storage key; always `metricNameToKey(label)`, so the existing name -> key pipeline yields it. */
  key: string;
  /** Name shown in the dropdown and stored as the metric's name. */
  label: string;
  /** Other keys the same metric goes by (e.g. in an uploaded Croissant file). */
  aliases?: readonly string[];
}

const SENSITIVITY: EvaluatorMetricOption = {
  key: 'sensitivity',
  label: 'Sensitivity',
  aliases: ['sensitivity_recall', 'recall', 'tpr', 'true_positive_rate'],
};
const SPECIFICITY: EvaluatorMetricOption = {
  key: 'specificity',
  label: 'Specificity',
  aliases: ['specificity_true_negative_rate', 'tnr', 'true_negative_rate'],
};
const PPV: EvaluatorMetricOption = {
  key: 'ppv',
  label: 'PPV',
  aliases: ['ppv_positive_predictive_value', 'precision', 'positive_predictive_value'],
};
const NPV: EvaluatorMetricOption = {
  key: 'npv',
  label: 'NPV',
  aliases: ['npv_negative_predictive_value', 'negative_predictive_value'],
};
const ACCURACY: EvaluatorMetricOption = { key: 'accuracy', label: 'Accuracy' };
const AUC: EvaluatorMetricOption = {
  key: 'auc',
  label: 'AUC',
  aliases: ['auc_area_under_curve', 'roc_auc', 'auroc'],
};

const BINARY_CLASSIFICATION_METRICS: readonly EvaluatorMetricOption[] = [
  ACCURACY,
  SENSITIVITY,
  SPECIFICITY,
  PPV,
  NPV,
  { key: 'fnr', label: 'FNR', aliases: ['fnr_false_negative_rate', 'false_negative_rate'] },
  { key: 'fpr', label: 'FPR', aliases: ['fpr_false_positive_rate', 'false_positive_rate'] },
  { key: 'f1', label: 'F1', aliases: ['f1_score', 'f1score'] },
  { key: 'f2', label: 'F2', aliases: ['f2_score', 'f2score'] },
  AUC,
];

const MULTICLASS_CLASSIFICATION_METRICS: readonly EvaluatorMetricOption[] = [
  ACCURACY,
  { key: 'macro_f1', label: 'Macro F1' },
  { key: 'weighted_f1', label: 'Weighted F1' },
  { key: 'macro_f2', label: 'Macro F2' },
  { key: 'weighted_f2', label: 'Weighted F2' },
  SENSITIVITY,
  SPECIFICITY,
  PPV,
  NPV,
  {
    key: 'qwk',
    label: 'QWK',
    aliases: ['qwk_quadratic_weight_kappa', 'qwk_quadratic_weighted_kappa', 'quadratic_weighted_kappa'],
  },
  AUC,
];

const SEGMENTATION_METRICS: readonly EvaluatorMetricOption[] = [
  { key: 'dice', label: 'Dice', aliases: ['dice_score', 'dice_coefficient', 'mean_dice'] },
  { key: 'iou', label: 'IoU', aliases: ['iou_score', 'jaccard', 'jaccard_index', 'mean_iou'] },
  { key: 'global_dice', label: 'Global Dice' },
  { key: 'global_iou', label: 'Global IoU' },
  SENSITIVITY,
  PPV,
  SPECIFICITY,
  { key: 'pixel_accuracy', label: 'Pixel Accuracy' },
];

const BOUNDING_BOX_METRICS: readonly EvaluatorMetricOption[] = [
  {
    key: 'map_50_95',
    label: 'mAP@[0.5:0.95]',
    aliases: ['map', 'map_0_5_0_95', 'map_0_50_0_95', 'mean_average_precision', 'coco_map'],
  },
  {
    key: 'map_50_95_giou',
    label: 'mAP@[0.5:0.95]_GIoU',
    aliases: [
      'map_giou',
      'giou_map',
      'giou_map_50_95',
      'giou_matched_map',
      'giou_matched_map_50_95',
      'giou_matched_map_0_5_0_95',
      'map_giou_0_5_0_95',
    ],
  },
  { key: 'ap_50', label: 'AP@0.5', aliases: ['ap50', 'ap_0_5', 'ap_0_50'] },
  { key: 'ap_75', label: 'AP@0.75', aliases: ['ap75', 'ap_0_75'] },
  { key: 'precision_50', label: 'Precision@0.5', aliases: ['precision_0_5'] },
  { key: 'recall_50', label: 'Recall@0.5', aliases: ['recall_0_5', 'ar_50', 'ar50', 'ar_0_5'] },
  { key: 'f1_50', label: 'F1@0.5', aliases: ['f1_0_5'] },
  { key: 'precision_75', label: 'Precision@0.75', aliases: ['precision_0_75'] },
  { key: 'recall_75', label: 'Recall@0.75', aliases: ['recall_0_75', 'ar_75', 'ar75', 'ar_0_75'] },
  { key: 'f1_75', label: 'F1@0.75', aliases: ['f1_0_75'] },
  { key: 'detection_rate', label: 'Detection Rate' },
  { key: 'avg_boxes_per_image', label: 'Avg Boxes Per Image' },
  { key: 'mean_iou_all_images', label: 'Mean IoU (All Images)', aliases: ['mean_iou'] },
  { key: 'median_iou_all_images', label: 'Median IoU (All Images)', aliases: ['median_iou'] },
  { key: 'p25_iou_all_images', label: 'P25 IoU (All Images)', aliases: ['p25_iou'] },
  { key: 'mean_giou_all_images', label: 'Mean GIoU (All Images)', aliases: ['mean_giou'] },
];

/** Metrics the evaluator for `problemStatement` reports; empty when the problem statement is unknown. */
export function getEvaluatorMetricOptions(problemStatement: unknown): readonly EvaluatorMetricOption[] {
  switch (String(problemStatement ?? '').trim()) {
    case 'binary_classification':
      return BINARY_CLASSIFICATION_METRICS;
    case 'multiclass_classification':
      return MULTICLASS_CLASSIFICATION_METRICS;
    case 'segmentation':
      return SEGMENTATION_METRICS;
    case 'bounding_box':
      return BOUNDING_BOX_METRICS;
    default:
      return [];
  }
}

/**
 * The dropdown option a metric name stands for under `problemStatement` -- by label, key or alias,
 * so a name from an uploaded Croissant file (`Sensitivity (Recall)`) lands on its option -- or
 * undefined when that problem statement's evaluator does not report it.
 */
export function resolveEvaluatorMetric(
  problemStatement: unknown,
  name: unknown,
): EvaluatorMetricOption | undefined {
  const key = metricNameToKey(String(name ?? ''));
  if (!key) {
    return undefined;
  }
  return getEvaluatorMetricOptions(problemStatement).find(
    (option) => option.key === key || !!option.aliases?.includes(key),
  );
}
